import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  IoArrowForward,
  IoPersonOutline,
  IoLockClosedOutline,
  IoEyeOutline,
  IoEyeOffOutline,
  IoLanguageOutline,
  IoSunny,
  IoMoon,
  IoPhonePortrait,
  IoStorefrontOutline,
  IoRibbonOutline,
  IoBriefcaseOutline,
} from "react-icons/io5";
import { useAuth } from "@/auth";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { useTheme } from "@/theme";
import { ThemeDialog } from "@/components/ThemeDialog";
import { tapSuccess, tapError } from "@/utils/haptics";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, effective } = useTheme();
  const [role, setRole] = useState("titolare"); // "titolare" | "direttore"
  const [storeId, setStoreId] = useState(ALL_STORES[0]);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !password.trim()) {
      toast.error(t("login_error"));
      return;
    }
    if (role === "direttore" && !storeId) {
      toast.error(t("select_your_store"));
      return;
    }
    setLoading(true);
    try {
      await login(name.trim(), password.trim(), role, role === "direttore" ? storeId : null);
      tapSuccess();
    } catch (err) {
      tapError();
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Distinguish between server errors, wrong credentials, and network problems
      // so mobile users on flaky cellular can tell the difference at a glance.
      if (!err?.response) {
        // No HTTP response at all → real connectivity issue.
        toast.error("Connessione assente o instabile. Passa al Wi-Fi o riprova.");
      } else if (status === 401) {
        toast.error(detail || "Password errata");
      } else if (status >= 500) {
        toast.error("Server temporaneamente non disponibile. Riprova.");
      } else {
        toast.error(detail || t("login_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const disabled = !name.trim() || !password.trim() || loading || (role === "direttore" && !storeId);

  return (
    <div className="mobile-frame" style={{ paddingBottom: 0 }} data-testid="login-page">
      <div className="tf-login-container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            type="button"
            data-testid="login-theme-toggle"
            className="tf-icon-btn yellow"
            onClick={() => setThemeOpen(true)}
            aria-label={t("theme")}
            title={t("theme")}
          >
            {theme === "auto" ? <IoPhonePortrait size={20} /> : effective === "dark" ? <IoMoon size={20} /> : <IoSunny size={20} />}
          </button>
          <button
            type="button"
            data-testid="login-lang-toggle"
            className="tf-icon-btn yellow"
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            style={{ minWidth: 62, borderRadius: 20, gap: 4, padding: "0 10px" }}
            title={t("language")}
          >
            <IoLanguageOutline size={18} />
            <span style={{ fontWeight: 800, fontSize: 14 }}>{lang.toUpperCase()}</span>
          </button>
        </div>

        <div className="tf-brand-pill" data-testid="brand-logo">THE FIRST</div>
        <div className="tf-yellow-underline-lg" />

        <h1 className="tf-h1" style={{ marginBottom: 6 }}>{t("login_title")}</h1>
        <p className="tf-muted" style={{ textAlign: "center", marginTop: 0, fontSize: 15 }}>{t("login_subtitle")}</p>

        <form onSubmit={submit} style={{ marginTop: 24 }}>
          <div className="tf-role-toggle" role="tablist" data-testid="role-toggle">
            <button
              type="button"
              role="tab"
              aria-selected={role === "titolare"}
              className={`tf-role-tab ${role === "titolare" ? "active" : ""}`}
              onClick={() => setRole("titolare")}
              data-testid="role-titolare"
            >
              <IoBriefcaseOutline size={18} />
              {t("role_owner")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={role === "direttore"}
              className={`tf-role-tab ${role === "direttore" ? "active" : ""}`}
              onClick={() => setRole("direttore")}
              data-testid="role-direttore"
            >
              <IoRibbonOutline size={18} />
              {t("role_manager")}
            </button>
          </div>

          <div className="tf-field">
            <label className="tf-label">{t("your_name")}</label>
            <div className="tf-input-wrap">
              <IoPersonOutline size={20} className="tf-input-icon-left" />
              <input
                data-testid="login-name-input"
                className="tf-input tf-input-with-icon-left"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("your_name_placeholder")}
                autoComplete="name"
              />
            </div>
          </div>

          {role === "direttore" && (
            <div className="tf-field">
              <label className="tf-label">{t("select_your_store")}</label>
              <div className="tf-input-wrap">
                <IoStorefrontOutline size={20} className="tf-input-icon-left" />
                <select
                  data-testid="login-store-select"
                  className="tf-select tf-input-with-icon-left"
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                >
                  {ALL_STORES.map((s) => (
                    <option key={s} value={s}>{t(STORE_LABELS[s])}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="tf-field">
            <label className="tf-label">{t("password")}</label>
            <div className="tf-input-wrap">
              <IoLockClosedOutline size={20} className="tf-input-icon-left" />
              <input
                data-testid="login-password-input"
                className="tf-input tf-input-with-icon-left tf-input-with-icon-right"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("password_placeholder")}
                autoComplete="current-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="tf-input-icon-right"
                onClick={() => setShowPwd((s) => !s)}
                data-testid="login-password-toggle"
                aria-label="Show/hide password"
              >
                {showPwd ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
              </button>
            </div>
          </div>

          <button
            data-testid="login-submit-button"
            type="submit"
            className="tf-btn-primary"
            disabled={disabled}
            style={{ marginTop: 24 }}
          >
            {loading ? t("loading") : t("sign_in")}
            <IoArrowForward size={20} />
          </button>
        </form>
      </div>

      <ThemeDialog open={themeOpen} onOpenChange={setThemeOpen} />
    </div>
  );
}
